import * as angular from 'angular';
import * as _ from 'lodash';

import { ILoggerService, IStateService, IStoreService } from '../../../services';
import { ComponentDefinition, StateDto, State, Group, ObjectUser, AlarmList, StateViewModel } from '../../../models';
import { IRootScope } from '../../../IRootScope';
import { isEditMode, Helper } from '../../../common';
import { Store } from '../../../stateManagement/Store';
import { StoreElement } from '../../../stateManagement';

class StatesTilesController implements angular.IComponentController, angular.IOnDestroy {
    alarms: AlarmList;
    isEditMode: boolean;
    isLoading: boolean;
    loadingMessage: string;
    selectedItem: StateViewModel;
    stateList: StateViewModel[];

    readonly formController: angular.IFormController;

    private _unsubscribe: () => void;

    static $inject = [
        '$rootScope',
        '$scope',
        '$translate',
        'storeService',
        'stateService',
        'loggerService'
    ];

    constructor(
        private _$rootScope: IRootScope,
        private _$scope: angular.IScope,
        private _$translate: angular.translate.ITranslateService,
        private _storeService: IStoreService,
        private _stateService: IStateService,
        private _loggerService: ILoggerService
    ) { }

    $onInit() {
        this.isEditMode = this._$rootScope.isEditMode;

        this._$scope.$on(isEditMode, (_e, data) => this.isEditMode = data);

        this._unsubscribe = this._storeService.register(
            store => this.refreshStore(store),
            [
                StoreElement.States,
                StoreElement.Groups,
                StoreElement.Alarms
            ]
        );
    }

    $onDestroy(): void {
        if (this._unsubscribe) {
            this._unsubscribe();
        }
    }

    drop() {
        const state = {
            shortComment: '',
            name: '',
            guid: Helper.getGuid(),
            marker: '',
            markerColorString: '',
            classed: {}
        };

        this.createState(state as any);
    }

    deleteItem(guid: string): void {
        this._storeService.dispatchers().states.deleteOne(guid)
            .then(() => {
                this.selectedItem = null;
                this._loggerService.logSuccess('State removed', true);
            })
            .catch(_reason => {
                this._loggerService.logError('Cannot remove state', true);
            });
    }

    select(item: any, $event: angular.IAngularEvent) {
        if (this.isEditMode) {
            if (this.formController && this.formController.$dirty) {
                const msg = this._$translate.instant('Save or cancel changes before selecting another state.');
                this._loggerService.logWarning(msg);
                $event.stopPropagation();
            } else {
                this.selectedItem = item;
            }
            if ($event.preventDefault) {
                $event.preventDefault();
            }
        }
    }

    unselect($event) {
        if (!$event.isDefaultPrevented()) {
            this.selectedItem = null;
        }
    }

    getClass(state: StateDto & { classed: { [index: string]: boolean } }): any {
        const result: any = state.classed || {};
        result['bordered'] = this.selectedItem.state.guid === state.state.guid && this.isEditMode;
        return result;
    }

    private mapStateViewModel(state: State, groupList: Group[]): StateViewModel {
        const users = this.computeStateUsers(state, groupList);
        const errors = this._stateService.isErrors(state);
        const result: any = {};
        if (errors && errors.length > 0) {
            errors.forEach(e => {
                result[e.classed] = true;
            });
        }
        return {
            classed: result,
            error: _.first(errors),
            groups: '',
            icon: this._stateService.getIcon(state.icon),
            identifiers: [],
            isUsed: (groupList && groupList.length > 0) ? users.length > 0 : true,
            state: state,
            style: { 'background-color': state.markerColorString },
            url: '',
            users: users
        };
    }

    private refreshStore(store: Store) {
        const { states, groups, alarms } = store;
        const groupList: Group[] = _.values(groups.dictionary);

        const isLoading = states.isLoading && groups.isLoading && alarms.isLoading;
        if (isLoading) {
            return { isLoading: true };
        }

        const stateList = _.values(states.dictionary)
            .map((state: State) => this.mapStateViewModel(state, groupList))
            .sort((a, b) => a.state.name > b.state.name ? 1 : -1);
        if (this.selectedItem && stateList) {
            const found = stateList.find(stateVm => stateVm.state.guid === this.selectedItem.state.guid);
            if (found) {
                this.selectedItem = found;
            }
        }
        this.alarms = alarms.dictionary;
        this.isLoading = isLoading;
        this.stateList = stateList;
    }

    private computeStateUsers(state: State, groups: Group[]): ObjectUser[] {
        if (groups && groups.length > 0) {
            const inGroup = groups
                .filter(g => _.some(g.groupStateCollection, gs => gs.stateGuid === state.guid))
                .map(g => this.createGroupUsage(g, 'Rules'));


            const inAlarms = this.alarms ?
                groups
                    .filter(g => this.hasAlarmWithState(state.guid, g))
                    .map(g => this.createGroupUsage(g, 'Alarms'))
                : [];

            return inGroup.concat(inAlarms);
        }

        return [];
    }

    private hasAlarmWithState(stateGuid: string, group: Group): boolean {
        try {
            return _
                .chain(group.identifierCollection.map(i => this.alarms[i.guid]))
                .flatten()
                .filter(a => a !== undefined)
                .map(a => a.states)
                .flatten()
                .some(g => g === stateGuid)
                .value();
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    private createGroupUsage(group: Group, ownerType: string): ObjectUser {
        return {
            guid: group.guid,
            name: group.uniqueName,
            ownerGuid: group.guid,
            ownerName: group.name,
            ownerType: ownerType,
            type: 'Group',
            isDeletable: false
        };
    }

    private createState(state: State) {
        const stateDto: StateViewModel = {
            groups: '',
            identifiers: null,
            isUsed: false,
            state: state,
            style: '',
            url: '',
            users: []
        };
        this.stateList.push(stateDto);
        this.selectedItem = stateDto;
        let scrolled = false;
        const listener = this._$scope.$watch(() => {
            if (!scrolled) {
                const element = document.getElementById(`item${stateDto.state.guid}`);
                if (element) {
                    element.scrollIntoView();
                    scrolled = true;
                    listener();
                }
            }
        });
    }
}

export const wtStatesTiles: ComponentDefinition = {
    componentId: 'wtStatesTiles',
    controller: StatesTilesController,
    template: require('./statesTiles.component.html'),
    controllerAs: 'vm',
    bindings: {
        formController: '<?',
        selectedItem: '=?'
    }
};
